import type {
  Announcement,
  AuditLog,
  CalendarEvent,
  Campaign,
  CampaignStatus,
  Channel,
  CursorPaginatedResult,
  Document,
  DocumentCategory,
  DocumentSignatureMethod,
  DuePeriod,
  DuesPeriodSummary,
  DuesRosterEntry,
  EventPhoto,
  EventRegistration,
  ExpenseRecord,
  FinanceCategory,
  IncomeRecord,
  Invitation,
  ManualDuePaymentMethod,
  Membership,
  MembershipDue,
  Message,
  Notification,
  OrgDuesSettings,
  Organization,
  Project,
  ProjectPriority,
  ProjectStatus,
  RoleName,
  ServiceRequest,
  ServiceRequestComment,
  ServiceRequestStatus,
  SupplyConfig,
  SupplyReservation,
  SupplyOffering,
  AgreementInfo,
  UUID,
} from '@zetyar/domain'

export interface TenantContext {
  readonly authUid: string
  readonly personId: UUID
  readonly organizationId: UUID
  readonly membershipId?: UUID
}

export interface IdentityContext {
  readonly authUid: string
  readonly personId: UUID
}

export interface OrganizationMembership {
  readonly organization: Organization
  readonly membership: Membership
}

export interface OrganizationRepository {
  listForPerson(context: IdentityContext): Promise<OrganizationMembership[]>
}

export interface AnnouncementListInput {
  readonly cursor?: string
  readonly limit: number
}

export interface CreateAnnouncementInput {
  readonly title: string
  readonly content: string
  readonly summary?: string
  readonly isPinned?: boolean
  readonly isDraft?: boolean
  readonly audienceType?: string
}

export interface UpdateAnnouncementInput {
  readonly title?: string
  readonly content?: string
  readonly summary?: string
  readonly isPinned?: boolean
  readonly isDraft?: boolean
}

export interface AnnouncementRepository {
  listVisible(
    context: TenantContext,
    input: AnnouncementListInput,
  ): Promise<CursorPaginatedResult<Announcement>>

  acknowledgeRead(
    context: TenantContext,
    announcementId: UUID,
  ): Promise<void>

  create?(
    context: TenantContext,
    input: CreateAnnouncementInput,
  ): Promise<Announcement>

  update?(
    context: TenantContext,
    announcementId: UUID,
    input: UpdateAnnouncementInput,
  ): Promise<Announcement>

  delete?(
    context: TenantContext,
    announcementId: UUID,
  ): Promise<void>
}

export interface MemberListInput {
  readonly limit: number
}

export interface MemberRepository {
  list(
    context: TenantContext,
    input: MemberListInput,
  ): Promise<Membership[]>

  updateStatus?(
    context: TenantContext,
    membershipId: UUID,
    status: 'active' | 'inactive' | 'suspended',
  ): Promise<void>

  updateDetails?(
    context: TenantContext,
    membershipId: UUID,
    details: {
      displayName?: string
      position?: string
      department?: string
      institutionalEmail?: string
      role?: string
    },
  ): Promise<void>

  assignRole?(
    context: TenantContext,
    membershipId: UUID,
    role: RoleName,
  ): Promise<void>

  revokeRole?(
    context: TenantContext,
    membershipId: UUID,
    membershipRoleId: UUID,
    role: RoleName,
  ): Promise<void>
}

export interface DocumentListInput {
  readonly limit: number
  readonly categoryId?: UUID
}

export interface CreateDocumentInput {
  readonly title: string
  readonly description?: string
  readonly fileUrl: string
  readonly fileName: string
  readonly fileSize: number
  readonly mimeType: string
  readonly categoryId?: UUID
  readonly version?: string
  readonly isPublic?: boolean
}

/** A PDF composed in the app and signed by its author (0038_document_signatures.sql). */
export interface CreateSignedDocumentInput {
  readonly title: string
  readonly description?: string
  readonly fileUrl: string
  readonly fileName: string
  readonly fileSize: number
  readonly categoryId?: UUID
  readonly method: DocumentSignatureMethod
  readonly signerName: string
  readonly signerPosition?: string
  /** SHA-256 (hex) of the uploaded file. */
  readonly fileSha256: string
}

export interface DocumentRepository {
  listVisible(
    context: TenantContext,
    input: DocumentListInput,
  ): Promise<Document[]>

  createSigned?(
    context: TenantContext,
    input: CreateSignedDocumentInput,
  ): Promise<Document>

  /** Tags available in the organization (document_categories). */
  listCategories?(context: TenantContext): Promise<DocumentCategory[]>

  createCategory?(context: TenantContext, name: string): Promise<DocumentCategory>

  /** Short-lived signed URL: objects live in a private bucket and are never linked directly. */
  getDownloadUrl?(context: TenantContext, storagePath: string): Promise<string>

  create?(
    context: TenantContext,
    input: CreateDocumentInput,
  ): Promise<Document>

  delete?(
    context: TenantContext,
    documentId: UUID,
  ): Promise<void>
}

export interface EventListInput {
  readonly limit: number
  /** ISO datetime: only events that have not ended before this moment. */
  readonly fromDate?: string
}

export interface CreateEventInput {
  readonly title: string
  readonly description?: string
  readonly location?: string
  readonly isVirtual?: boolean
  readonly virtualUrl?: string
  readonly startDate: string
  readonly endDate: string
  readonly capacity?: number
}

export interface UpdateEventInput {
  readonly title?: string
  readonly description?: string
  readonly location?: string
  readonly isVirtual?: boolean
  readonly virtualUrl?: string
  readonly startDate?: string
  readonly endDate?: string
  readonly capacity?: number
}

export interface RegisterEventPhotoInput {
  readonly eventId: UUID
  readonly storagePath: string
  readonly thumbPath: string
  readonly byteSize: number
  readonly thumbByteSize: number
  readonly width: number
  readonly height: number
  readonly caption?: string
}

export interface EventRepository {
  listVisible(
    context: TenantContext,
    input: EventListInput,
  ): Promise<CalendarEvent[]>

  listPhotos?(context: TenantContext, eventId: UUID): Promise<EventPhoto[]>

  registerPhoto?(context: TenantContext, input: RegisterEventPhotoInput): Promise<EventPhoto>

  deletePhoto?(context: TenantContext, photoId: UUID): Promise<void>

  /** Batch of short-lived signed URLs keyed by storage path (the bucket is private). */
  getPhotoUrls?(context: TenantContext, paths: readonly string[]): Promise<Record<string, string>>

  /** Signed URL served as an attachment, so the browser downloads it under `fileName`. */
  getPhotoDownloadUrl?(context: TenantContext, path: string, fileName: string): Promise<string>

  register?(
    context: TenantContext,
    eventId: UUID,
  ): Promise<EventRegistration>

  create?(
    context: TenantContext,
    input: CreateEventInput,
  ): Promise<CalendarEvent>

  update?(
    context: TenantContext,
    eventId: UUID,
    input: UpdateEventInput,
  ): Promise<CalendarEvent>

  delete?(
    context: TenantContext,
    eventId: UUID,
  ): Promise<void>

  recordAttendanceQR?(
    context: TenantContext,
    registrationId: UUID,
  ): Promise<void>
}

export interface ChatChannelListInput {
  readonly limit: number
}

export interface ChatMessageListInput {
  readonly channelId: UUID
  readonly limit: number
}

export interface SendMessageInput {
  readonly channelId: UUID
  readonly content: string
  readonly replyToId?: UUID
}

export interface CreateChannelInput {
  readonly name: string
  readonly description?: string
  readonly type?: string
}

export interface ChatRepository {
  listChannels(
    context: TenantContext,
    input: ChatChannelListInput,
  ): Promise<Channel[]>

  listMessages(
    context: TenantContext,
    input: ChatMessageListInput,
  ): Promise<Message[]>

  sendMessage(
    context: TenantContext,
    input: SendMessageInput,
  ): Promise<Message>

  createChannel?(
    context: TenantContext,
    input: CreateChannelInput,
  ): Promise<Channel>

  deleteChannel?(
    context: TenantContext,
    channelId: UUID,
  ): Promise<void>

  getUnreadChannelIds?(
    context: TenantContext,
  ): Promise<UUID[]>

  markChannelRead?(
    context: TenantContext,
    channelId: UUID,
  ): Promise<void>

  moderateMessage?(
    context: TenantContext,
    messageId: UUID,
  ): Promise<void>
}

export interface InvitationListInput {
  readonly limit: number
}

export interface CreateInvitationInput {
  readonly email: string
  readonly role: string
  readonly expiresInDays?: number
}

export interface AcceptInvitationInput {
  readonly token: string
  readonly authUid: string
  readonly displayName: string
}

export interface AcceptedInvitation {
  readonly organizationId: UUID
  readonly membershipId: UUID
}

export interface InvitationRepository {
  listPending(
    context: TenantContext,
    input: InvitationListInput,
  ): Promise<Invitation[]>

  create(
    context: TenantContext,
    input: CreateInvitationInput,
  ): Promise<Invitation>

  revoke(
    context: TenantContext,
    invitationId: UUID,
  ): Promise<void>

  getByToken(token: string): Promise<Invitation | null>

  accept(input: AcceptInvitationInput): Promise<AcceptedInvitation>
}

export interface ProjectListInput {
  readonly limit: number
}

export interface CreateProjectInput {
  readonly title: string
  readonly description?: string
  readonly priority?: ProjectPriority
  readonly responsibleMembershipId?: UUID
  readonly startDate?: string
  readonly targetDate?: string
}

export interface UpdateProjectInput {
  readonly title?: string
  readonly description?: string
  readonly status?: ProjectStatus
  readonly priority?: ProjectPriority
  readonly progress?: number
  readonly responsibleMembershipId?: UUID
  readonly startDate?: string
  readonly targetDate?: string
}

export interface ProjectRepository {
  listVisible(
    context: TenantContext,
    input: ProjectListInput,
  ): Promise<Project[]>

  create?(
    context: TenantContext,
    input: CreateProjectInput,
  ): Promise<Project>

  update?(
    context: TenantContext,
    projectId: UUID,
    input: UpdateProjectInput,
  ): Promise<void>

  delete?(
    context: TenantContext,
    projectId: UUID,
  ): Promise<void>
}

export interface FinanceOverview {
  readonly categories: FinanceCategory[]
  readonly income: IncomeRecord[]
  readonly expenses: ExpenseRecord[]
}

// ─── Dues / Cuotas sociales ─────────────────────────────
// Every mutation is a server-side RPC (0033_membership_dues.sql) that checks role, writes the income
// record and audit log atomically. Errors surface as stable `dues_*` codes in the error message.

export interface SaveDuesSettingsInput {
  readonly monthlyAmount: number
  readonly currency: string
  readonly dueDay: number
  readonly bankName?: string | null
  readonly accountType?: string | null
  readonly accountNumber?: string | null
  readonly accountHolder?: string | null
  readonly accountRut?: string | null
  readonly accountEmail?: string | null
  readonly instructions?: string | null
  readonly onlinePaymentEnabled: boolean
}

export type DuesRosterStatusFilter = 'pending' | 'paid' | 'exempt'

export interface DuesRosterInput {
  readonly period: DuePeriod
  readonly search?: string
  readonly status?: DuesRosterStatusFilter
  readonly limit: number
  readonly offset: number
}

export interface DuesRosterPage {
  readonly entries: DuesRosterEntry[]
  readonly total: number
}

export interface RecordDuePaymentInput {
  readonly membershipId: UUID
  readonly startPeriod: DuePeriod
  readonly months: number
  readonly method: ManualDuePaymentMethod
  readonly notes?: string
}

export interface SetDueExemptionInput {
  readonly membershipId: UUID
  readonly period: DuePeriod
  readonly exempt: boolean
  readonly notes?: string
}

export interface StartDueOnlinePaymentInput {
  readonly startPeriod: DuePeriod
  readonly months: number
}

export interface DuesRepository {
  getSettings(context: TenantContext): Promise<OrgDuesSettings | null>

  listMemberDues(
    context: TenantContext,
    membershipId: UUID,
    input: { readonly limit: number },
  ): Promise<MembershipDue[]>

  getPeriodSummary?(context: TenantContext, period: DuePeriod): Promise<DuesPeriodSummary>

  saveSettings?(context: TenantContext, input: SaveDuesSettingsInput): Promise<OrgDuesSettings>

  listRoster?(context: TenantContext, input: DuesRosterInput): Promise<DuesRosterPage>

  recordPayment?(context: TenantContext, input: RecordDuePaymentInput): Promise<UUID>

  revertPayment?(context: TenantContext, duePaymentId: UUID, reason?: string): Promise<void>

  setExemption?(context: TenantContext, input: SetDueExemptionInput): Promise<void>

  /** Holds the months server-side and returns the Mercado Pago checkout URL. */
  startOnlinePayment?(
    context: TenantContext,
    input: StartDueOnlinePaymentInput,
  ): Promise<{ readonly initPoint: string }>
}

export interface CreateIncomeInput {
  readonly amount: number
  readonly currency?: string
  readonly description: string
  readonly source?: string
  readonly date: string
  readonly categoryId?: UUID
}

export interface CreateExpenseInput {
  readonly amount: number
  readonly currency?: string
  readonly description: string
  readonly vendor?: string
  readonly date: string
  readonly categoryId?: UUID
}

export interface FinanceRepository {
  listOverview(
    context: TenantContext,
    input: { readonly limit: number },
  ): Promise<FinanceOverview>

  createIncome?(
    context: TenantContext,
    input: CreateIncomeInput,
  ): Promise<void>

  deleteIncome?(
    context: TenantContext,
    incomeId: UUID,
  ): Promise<void>

  createExpense?(
    context: TenantContext,
    input: CreateExpenseInput,
  ): Promise<void>

  decideExpense?(
    context: TenantContext,
    expenseId: UUID,
    createdByMembershipId: UUID,
    decision: 'approved' | 'rejected',
  ): Promise<void>

  deleteExpense?(
    context: TenantContext,
    expenseId: UUID,
  ): Promise<void>
}

export interface SupplyListInput {
  readonly limit: number
}

export interface CreateSupplyOfferingInput {
  readonly productName: string
  readonly productUnit?: string
  readonly title: string
  readonly description?: string
  readonly unitPrice: number
  readonly currency?: string
  readonly totalStock: number
  readonly maxPerUser?: number
  readonly startDate: string
  readonly endDate: string
}

export interface SupplyRepository {
  listVisibleOfferings(
    context: TenantContext,
    input: SupplyListInput,
  ): Promise<SupplyOffering[]>

  /**
   * Reserves stock and creates a real Mercado Pago payment preference for it (via the
   * create-reservation-preference Edge Function - see the payments plan). Returns the checkout
   * URL the caller must redirect the browser to; the reservation only moves to `paid` once
   * Mercado Pago's webhook confirms the payment (mercadopago-webhook /
   * mark_reservation_paid_by_gateway), never client-side.
   */
  reserve?(
    context: TenantContext,
    offeringId: UUID,
    quantity: number,
  ): Promise<{ initPoint: string }>

  createOffering?(
    context: TenantContext,
    input: CreateSupplyOfferingInput,
  ): Promise<void>

  deleteOffering?(
    context: TenantContext,
    offeringId: UUID,
  ): Promise<void>

  updateOffering?(
    context: TenantContext,
    offeringId: UUID,
    input: {
      title: string
      description?: string | null
      unitPrice: number
      totalStock: number
      remainingStock: number
      maxPerUser?: number
      startDate: string
      endDate: string
    },
  ): Promise<void>

  listReservations?(
    context: TenantContext,
    input: SupplyListInput,
  ): Promise<SupplyReservation[]>

  listMyReservations?(
    context: TenantContext,
    limit: number,
  ): Promise<SupplyReservation[]>

  markDelivered?(
    context: TenantContext,
    reservationId: UUID,
  ): Promise<void>

  cancelReservation?(
    context: TenantContext,
    reservationId: UUID,
  ): Promise<void>

  getSupplyConfig?(
    context: TenantContext,
  ): Promise<SupplyConfig | null>

  saveSupplyConfig?(
    context: TenantContext,
    input: Omit<SupplyConfig, 'organizationId'>,
  ): Promise<void>

  listAgreementInfos?(
    context: TenantContext,
    limit: number,
  ): Promise<AgreementInfo[]>

  createAgreementInfo?(
    context: TenantContext,
    input: {
      partnerName: string
      title: string
      description: string
      discountDetails?: string | null
      contactPhone?: string | null
      websiteUrl?: string | null
    },
  ): Promise<void>

  updateAgreementInfo?(
    context: TenantContext,
    id: UUID,
    input: {
      partnerName: string
      title: string
      description: string
      discountDetails?: string | null
      contactPhone?: string | null
      websiteUrl?: string | null
    },
  ): Promise<void>

  deleteAgreementInfo?(
    context: TenantContext,
    id: UUID,
  ): Promise<void>
}
export interface CampaignListInput {
  readonly limit: number
}

export interface CreateCampaignInput {
  readonly title: string
  readonly description: string
  readonly goalAmount: number
  readonly currency?: string
  readonly visibility?: 'members' | 'public'
  readonly startDate?: string
  readonly endDate?: string
}

export interface PendingContribution {
  readonly id: UUID
  readonly campaignId: UUID
  readonly campaignTitle: string
  readonly campaignRaisedAmount: number
  readonly campaignContributorCount: number
  readonly contributorMembershipId: UUID | null
  readonly amount: number
  readonly currency: string
  readonly isAnonymous: boolean
  readonly message: string | null
  readonly contributorName: string | null
  readonly createdAt: string
}

export interface CampaignRepository {
  listVisible(
    context: TenantContext,
    input: CampaignListInput,
  ): Promise<Campaign[]>

  /**
   * Creates a `pending` contribution and a real Mercado Pago payment preference for it (via the
   * create-contribution-preference Edge Function - see the payments plan). Returns the checkout
   * URL the caller must redirect the browser to; the contribution only moves to `paid` once
   * Mercado Pago's webhook confirms the payment (mercadopago-webhook /
   * confirm_contribution_by_gateway), never client-side. The pre-existing manual pledge +
   * confirmContribution/rejectContribution flow (for human-verified bank transfers) keeps working
   * unchanged alongside this.
   */
  contribute?(
    context: TenantContext,
    campaignId: UUID,
    amount: number,
    isAnonymous?: boolean,
    message?: string,
    payerEmail?: string,
  ): Promise<{ initPoint: string }>

  create?(
    context: TenantContext,
    input: CreateCampaignInput,
  ): Promise<void>

  updateStatus?(
    context: TenantContext,
    campaignId: UUID,
    status: CampaignStatus,
  ): Promise<void>

  delete?(
    context: TenantContext,
    campaignId: UUID,
  ): Promise<void>

  listPendingContributions?(
    context: TenantContext,
    input: CampaignListInput,
  ): Promise<PendingContribution[]>

  confirmContribution?(
    context: TenantContext,
    contribution: PendingContribution,
  ): Promise<void>

  rejectContribution?(
    context: TenantContext,
    contribution: PendingContribution,
  ): Promise<void>
}


export interface AppNotification {
  readonly id: UUID
  readonly type: string
  readonly title: string
  readonly body: string | null
  readonly resourceType: string | null
  readonly resourceId: UUID | null
  readonly readAt: string | null
  readonly createdAt: string
}

export interface NotificationListInput {
  readonly limit: number
}

/**
 * Only single-recipient notifications are wired so far (an action targets
 * one specific membership - status/role changes, expense and contribution
 * decisions). Broadcast notifications need a fan-out job or Cloud Function
 * that does not exist in this repository yet.
 */


// Sentinel used to scope a super admin's own profile assets (avatar) in Supabase Storage when
// they have no organization membership at all, so the org-scoped storage path still has a valid
// uuid segment. Never a real row in `organizations` - see supabase/migrations/0022_storage_platform_bypass.sql.
export const PLATFORM_ORGANIZATION_ID = '00000000-0000-0000-0000-000000000000'

export const PLATFORM_PRIMARY_DOMAIN = 'nexo.zetyar.com'
export const PLATFORM_HOSTING_DOMAIN = 'zetyar-nexo.pages.dev'
export const PLATFORM_RESERVED_DOMAINS = [
  PLATFORM_PRIMARY_DOMAIN,
  PLATFORM_HOSTING_DOMAIN,
  // Retired 2026-07-28: nexo.zetyar.com moved from Firebase Hosting to Cloudflare Pages. Kept
  // reserved so it can never be claimed as a customer domain even though nothing serves it now.
  'zetyar-nexo.web.app',
] as const

export function normalizeHostname(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[:/].*$/, '')
}

export function isPlatformReservedDomain(value: string): boolean {
  const hostname = normalizeHostname(value)
  return PLATFORM_RESERVED_DOMAINS.some((domain) => hostname === domain)
}

export function assertCustomerDomainAllowed(value: string): string {
  const hostname = normalizeHostname(value)
  if (!hostname) {
    throw new Error('El dominio no puede estar vacio.')
  }
  if (isPlatformReservedDomain(hostname)) {
    throw new Error('Ese dominio pertenece a la plataforma Zetyar Nexo y no se puede asignar a una empresa cliente.')
  }
  return hostname
}
export interface PlatformOrganizationDomain {
  readonly id: UUID
  readonly domain: string
  readonly isPrimary: boolean
  readonly status: 'pending' | 'active' | 'rejected' | 'disabled'
  readonly verificationToken: string
  readonly verifiedAt: string | null
  readonly createdAt: string
  readonly updatedAt: string
}

export interface PlatformOrganizationSummary {
  readonly id: UUID
  readonly name: string
  readonly slug: string
  readonly legalName: string | null
  readonly logoUrl: string | null
  readonly logoDarkUrl: string | null
  readonly faviconUrl: string | null
  readonly brandColor: string | null
  readonly primaryDomain: string | null
  readonly domains: PlatformOrganizationDomain[]
  readonly status: string
  readonly timezone: string
  readonly locale: string
  readonly createdAt: string
  readonly updatedAt: string
  readonly activeMemberCount: number
}


export interface SavePlatformOrganizationInput {
  readonly name: string
  readonly slug: string
  readonly legalName?: string | null
  readonly logoUrl?: string | null
  readonly logoDarkUrl?: string | null
  readonly faviconUrl?: string | null
  readonly brandColor?: string | null
  readonly primaryDomain?: string | null
  // Module flags are not here on purpose: each organization administers them from Settings (0036).
  readonly status: string
  readonly timezone: string
  readonly locale: string
}
export interface SaveOrganizationDomainInput {
  readonly domain: string
  readonly isPrimary: boolean
}

export interface PlatformMetrics {
  readonly organizationCount: number
  readonly personCount: number
}

export interface PlatformRepository {
  listAllOrganizations(
    context: IdentityContext,
    limit: number,
  ): Promise<PlatformOrganizationSummary[]>

  getMetrics(context: IdentityContext): Promise<PlatformMetrics>

  createOrganization?(
    context: IdentityContext,
    input: SavePlatformOrganizationInput,
  ): Promise<void>

  updateOrganization?(
    context: IdentityContext,
    id: UUID,
    input: SavePlatformOrganizationInput,
  ): Promise<void>

  createDomain?(
    context: IdentityContext,
    organizationId: UUID,
    input: SaveOrganizationDomainInput,
  ): Promise<void>

  verifyDomain?(
    context: IdentityContext,
    organizationId: UUID,
    domainId: UUID,
    isPrimary: boolean,
  ): Promise<void>

  disableDomain?(
    context: IdentityContext,
    organizationId: UUID,
    domainId: UUID,
  ): Promise<void>
}

// ─── SaaS leads (demo requests from the public landing) ─────
// Values mirror the checks on public.saas_leads (0034_saas_leads.sql).

export type SaasLeadOrganizationType = 'asociacion' | 'fundacion' | 'club' | 'comunidad' | 'otro'
export type SaasLeadMembersEstimate = '1-50' | '51-200' | '201-1000' | '1000+'
export type SaasLeadStatus = 'new' | 'contacted' | 'qualified' | 'discarded' | 'converted'

export interface SubmitSaasLeadInput {
  readonly fullName: string
  readonly email: string
  readonly phone?: string | null
  readonly organizationName: string
  readonly organizationType: SaasLeadOrganizationType
  readonly membersEstimate: SaasLeadMembersEstimate
  readonly message?: string | null
  readonly consent: boolean
  /** Honeypot field; real submissions leave it empty. */
  readonly website?: string
}

export interface SaasLead {
  readonly id: UUID
  readonly fullName: string
  readonly email: string
  readonly phone: string | null
  readonly organizationName: string
  readonly organizationType: SaasLeadOrganizationType
  readonly membersEstimate: SaasLeadMembersEstimate
  readonly message: string | null
  readonly consentAt: string
  readonly source: string
  readonly status: SaasLeadStatus
  readonly notes: string | null
  readonly handledByPersonId: UUID | null
  readonly createdAt: string
  readonly updatedAt: string
}

export interface SaasLeadListInput {
  readonly status?: SaasLeadStatus
  readonly limit: number
}

export interface UpdateSaasLeadInput {
  readonly status: SaasLeadStatus
  readonly notes?: string | null
}

export interface SaasLeadRepository {
  /** Anonymous: validated, rate-limited and stored by public.submit_saas_lead. */
  submit(input: SubmitSaasLeadInput): Promise<void>

  listForPlatform?(context: IdentityContext, input: SaasLeadListInput): Promise<SaasLead[]>

  countNew?(context: IdentityContext): Promise<number>

  update?(context: IdentityContext, id: UUID, input: UpdateSaasLeadInput): Promise<void>
}

export interface AuditListInput {
  readonly limit: number
}

export interface AuditRepository {
  list(
    context: TenantContext,
    input: AuditListInput,
  ): Promise<AuditLog[]>
}


export interface ActiveOrganizationResolutionInput {
  readonly currentOrganizationId: UUID | null
  readonly storedOrganizationId: UUID | null
  readonly organizationIds: readonly UUID[]
}

export function resolveActiveOrganizationId(input: ActiveOrganizationResolutionInput): UUID | null {
  const availableIds = new Set(input.organizationIds)

  if (input.currentOrganizationId && availableIds.has(input.currentOrganizationId)) {
    return input.currentOrganizationId
  }

  if (input.storedOrganizationId && availableIds.has(input.storedOrganizationId)) {
    return input.storedOrganizationId
  }

  return input.organizationIds[0] ?? null
}


export interface EventRegistrationValidationInput {
  readonly status: string
  readonly startDate: string
  readonly endDate: string
  readonly capacity: number | null
  readonly isRegistered: boolean
  readonly now?: Date
}

export function assertEventRegistrationAllowed(input: EventRegistrationValidationInput): void {
  const now = input.now ?? new Date()
  const endDate = new Date(input.endDate)

  if (input.isRegistered) {
    throw new Error('Membership is already registered for this event')
  }

  if (input.status !== 'published') {
    throw new Error('Event is not open for registration')
  }

  if (Number.isFinite(endDate.getTime()) && endDate < now) {
    throw new Error('Event has already ended')
  }

  if (input.capacity !== null && input.capacity <= 0) {
    throw new Error('Event has no available capacity')
  }
}

export interface RequestListInput {
  readonly limit: number
}

export interface CreateRequestInput {
  readonly subject: string
  readonly description: string
  readonly category?: string
}

export interface RequestRepository {
  listMine(
    context: TenantContext,
    input: RequestListInput,
  ): Promise<ServiceRequest[]>

  listForOrganization?(
    context: TenantContext,
    input: RequestListInput,
  ): Promise<ServiceRequest[]>

  get(
    context: TenantContext,
    requestId: UUID,
    commentLimit: number,
  ): Promise<{ request: ServiceRequest; comments: ServiceRequestComment[] } | null>

  create(
    context: TenantContext,
    input: CreateRequestInput,
  ): Promise<void>

  addComment(
    context: TenantContext,
    requestId: UUID,
    requestedById: UUID,
    content: string,
  ): Promise<void>

  updateStatus?(
    context: TenantContext,
    requestId: UUID,
    notifyMembershipId: UUID,
    status: ServiceRequestStatus,
  ): Promise<void>

  assign?(
    context: TenantContext,
    requestId: UUID,
    assignedToMembershipId: UUID,
  ): Promise<void>
}

export interface NotificationRepository {
  list(
    context: TenantContext,
    limit: number,
  ): Promise<Notification[]>

  getUnreadCount(
    context: TenantContext,
  ): Promise<number>

  markRead(
    context: TenantContext,
    notificationId: UUID,
  ): Promise<void>
}

export class TenantAccessError extends Error {
  constructor(message = 'Tenant context is not authorized for this organization') {
    super(message)
    this.name = 'TenantAccessError'
  }
}
